import httpClient from '../../../../api/httpClient';
import { 
  ItemLocation, 
  ItemLocationFilterParams,
  ItemLocationFormData
} from '../types/item-location.types';

export const itemLocationApi = {
  /**
   * Get paginated list of item locations
   */
  getItemLocations: async (params: ItemLocationFilterParams): Promise<ItemLocation[]> => {
    const response = await httpClient.get<ItemLocation[]>('/item-locations', { params });
    return response.data;
  },

  /**
   * Get all locations for a specific item
   */
  getItemLocationsByItemId: async (itemId: string, includeLocation: boolean = true): Promise<ItemLocation[]> => {
    const response = await httpClient.get<ItemLocation[]>(`/item-locations/item/${itemId}`, {
      params: { includeLocation }
    });
    return response.data;
  },

  /**
   * Get all items at a specific location
   */
  getItemLocationsByLocationId: async (locationId: string, includeItem: boolean = true): Promise<ItemLocation[]> => {
    const response = await httpClient.get<ItemLocation[]>(`/item-locations/location/${locationId}`, {
      params: { includeItem }
    });
    return response.data;
  },

  /**
   * Get specific item location by item, location, and batch combination
   */
  getItemLocationByItemLocationBatch: async (itemId: string, locationId: string, batchNumber: string): Promise<ItemLocation | null> => {
    try {
      console.log('🔍 Calling ItemLocation API:', `/item-locations/find/${itemId}/${locationId}/${batchNumber}`);
      const response = await httpClient.get<ItemLocation>(`/item-locations/find/${itemId}/${locationId}/${batchNumber}`);
      return response.data;
    } catch (error: any) {
      console.log('🔍 ItemLocation API error:', error.response?.status, error.response?.data);
      // If 404, return null (record doesn't exist)
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get a single item location by ID
   */
  getItemLocationById: async (id: string, includeItem: boolean = true, includeLocation: boolean = true): Promise<ItemLocation> => {
    const response = await httpClient.get<ItemLocation>(`/item-locations/${id}`, {
      params: { includeItem, includeLocation }
    });
    return response.data;
  },

  /**
   * Create a new item location
   */
  createItemLocation: async (data: ItemLocationFormData): Promise<ItemLocation> => {
    try {
      console.log('Sending item location data:', JSON.stringify(data, null, 2));
      const response = await httpClient.post<ItemLocation>('/item-locations', data, {
        validateStatus: (status) => status < 500
      });
      
      if (response.status >= 400) {
        console.error('Validation error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      };
      console.error('Create item location error details:', errorDetails);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  /**
   * Update an existing item location
   */
  updateItemLocation: async (id: string, data: Partial<ItemLocationFormData>): Promise<ItemLocation> => {
    try {
      console.log('Updating item location:', id, JSON.stringify(data, null, 2));
      const response = await httpClient.patch<ItemLocation>(`/item-locations/${id}`, data, {
        validateStatus: (status) => status < 500
      });
      
      if (response.status >= 400) {
        console.error('Validation error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      };
      console.error('Update item location error details:', errorDetails);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  /**
   * Update item location balance
   */
  updateBalance: async (id: string, balance: number): Promise<ItemLocation> => {
    const response = await httpClient.patch<ItemLocation>(`/item-locations/${id}/balance`, { balance });
    return response.data;
  },

  /**
   * Adjust item location balance (add/subtract)
   */
  adjustBalance: async (id: string, adjustment: number): Promise<ItemLocation> => {
    const response = await httpClient.patch<ItemLocation>(`/item-locations/${id}/adjust-balance`, { adjustment });
    return response.data;
  },

  /**
   * Delete an item location (soft delete)
   */
  deleteItemLocation: async (id: string): Promise<ItemLocation> => {
    const response = await httpClient.delete<ItemLocation>(`/item-locations/${id}`);
    return response.data;
  },

  /**
   * Get inventory summary for an item
   */
  getItemSummary: async (itemId: string): Promise<any> => {
    const response = await httpClient.get(`/item-locations/summary/item/${itemId}`);
    return response.data;
  },

  /**
   * Get inventory summary for a location
   */
  getLocationSummary: async (locationId: string): Promise<any> => {
    const response = await httpClient.get(`/item-locations/summary/location/${locationId}`);
    return response.data;
  },

  /**
   * Get items with low stock
   */
  getLowStockItems: async (threshold: number = 10): Promise<ItemLocation[]> => {
    const response = await httpClient.get<ItemLocation[]>('/item-locations/reports/low-stock', {
      params: { threshold }
    });
    return response.data;
  },

  /**
   * Get expired items
   */
  getExpiredItems: async (): Promise<ItemLocation[]> => {
    const response = await httpClient.get<ItemLocation[]>('/item-locations/reports/expired');
    return response.data;
  },

  /**
   * Get items expiring soon
   */
  getExpiringSoon: async (days: number = 30): Promise<ItemLocation[]> => {
    const response = await httpClient.get<ItemLocation[]>('/item-locations/reports/expiring-soon', {
      params: { days }
    });
    return response.data;
  },
};
